import React, { useState, useEffect } from "react";
import useGameBase from "../../hooks/useGameBase";
import SocketContext from "../../context/SocketContext";

const WhoAmIGame = ({ roomId, gameState, gamePlayers = [], user = {} }) => {
  const socket = React.useContext(SocketContext);
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, socket, roomId });

  const [guess, setGuess] = useState("");
  const [feedback, setFeedback] = useState(null);

  // Only clear the guess when the puzzle name changes — avoid clearing on new state object identities
  useEffect(() => setGuess(""), [gameState?.puzzle?.name]);

  if (!gameState) return <div className="p-4">Loading...</div>;

  const puzzle = gameState.puzzle || {};
  const clues = Array.isArray(puzzle.clues) ? puzzle.clues.slice(0, gameState.cluesReleased || 0) : [];
  const revealed = !!gameState.revealed;

  const maskName = (name) => {
    if (!name) return name;
    return name.split(' ').map(word => {
      let letterIndex = 0;
      return word.split('').map(ch => {
        if (/[A-Za-z]/.test(ch)) {
          const out = (letterIndex % 2 === 0) ? ch : '_';
          letterIndex += 1;
          return out;
        }
        // keep non-letter characters as-is (and don't count them)
        return ch;
      }).join('');
    }).join(' ');
  };

  const submitGuess = async () => {
    if (!guess.trim()) return;
    try {
      const ack = await submitAction({ type: "guessName", guess: guess.trim() });
      if (ack && ack.correct) {
        setFeedback(ack.points ? `correct:${ack.points}` : "correct");
      } else {
        setFeedback("incorrect");
      }
      setTimeout(() => setFeedback(null), 1500);
    } catch (e) {
      setFeedback("error");
      setTimeout(() => setFeedback(null), 1500);
    }
    setGuess("");
  };

  return (
    <div className="p-4 text-gray-900 dark:text-gray-100">
      <div className="max-w-xl mx-auto space-y-4">

        <div className="bg-white dark:bg-slate-800 rounded-lg p-4 shadow">
          <div className="mb-3">
            <strong>Clues:</strong>
          </div>
          <div className="mb-2 text-sm text-gray-600 dark:text-gray-300">
            {"Points available: "}
            {Math.max(1, 11 - Math.max(1, (gameState?.cluesReleased || 1)))}
          </div>
          <ol className="list-decimal ml-6 space-y-1">
            {clues.length === 0 && <li className="text-gray-500">No clues yet...</li>}
            {clues.map((c, i) => {
              const isLast = puzzle.clues && i === (puzzle.clues.length - 1);
              const showMasked = isLast && !revealed && String(c).trim() === String(puzzle.name).trim();
              return (
                <li key={i} className="text-sm">{showMasked ? maskName(String(c)) : c}</li>
              );
            })}
          </ol>
          {revealed && (
            <div className="mt-4 text-center">
              <p className="font-semibold">Answer:</p>
              <p className="text-lg font-bold">{puzzle.name}</p>
              {puzzle.image && (
                <img src={puzzle.image} alt={puzzle.name} className="mx-auto mt-2 max-h-40 rounded" />
              )}
            </div>
          )}
        </div>

        {!revealed && (
          <form
            className="bg-gray-100 dark:bg-slate-700 rounded-lg p-4 shadow"
            onSubmit={(e) => {
              e.preventDefault();
              submitGuess();
            }}
          >
            <label className="block mb-2 text-sm">Your guess</label>
            <div className="flex gap-2">
              <input
                value={guess}
                onChange={(e) => setGuess(e.target.value)}
                className="flex-1 p-2 rounded border dark:bg-slate-600 dark:text-gray-100 dark:border-slate-500"
                placeholder="Type a name and press Guess"
              />
              <button type="submit" className="bg-blue-600 text-white px-3 rounded">Guess</button>
            </div>
            {feedback && (
              <div className="mt-2 text-sm">
                {String(feedback).startsWith("correct") ? (
                  <span className="text-green-600">{String(feedback).includes(":") ? `Correct! +${String(feedback).split(":")[1]} pts` : "Correct!"}</span>
                ) : (
                  <span className="text-red-600">Incorrect</span>
                )}
              </div>
            )}
          </form>
        )}

      </div>
    </div>
  );
};

WhoAmIGame.Guide = function WhoAmIGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">Who Am I</h3>
      <p className="text-sm">A series of clues are revealed periodically. First to guess the person wins.</p>
    </div>
  );
};

export default WhoAmIGame;
